import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/cn'

interface Props {
  checked: boolean
  onChange: (next: boolean) => void
  label?: string
  disabled?: boolean
  /** Tamanho físico do switch. Default 'sm' (compacto, 18×30). */
  size?: 'sm' | 'md'
  /**
   * Tamanho da label de texto ao lado do switch.
   *   - 'md' (default): tamanho normal da app (text-sm, ~14px) — uso típico em forms
   *   - 'sm': tamanho compacto (text-[11px]) — usado em chips como "Sem meta"
   *
   * Independente do `size` físico do switch.
   */
  labelSize?: 'sm' | 'md'
  /** Mostra spinner DENTRO da bolinha enquanto uma ação assíncrona executa. */
  loading?: boolean
}

/**
 * Switch (toggle) compacto e acessível.
 *  - Default 'sm' (18×30px com handle 12px)
 *  - Cores trilho: brand-500 (on) / gray-300 (off)
 *  - Sem sombras (respeita regra global)
 *  - `loading`: renderiza um spinner DENTRO da bolinha (sem aumentar o tamanho)
 */
export function Toggle({ checked, onChange, label, disabled, size = 'sm', labelSize = 'md', loading }: Props) {
  const dims = size === 'sm'
    ? { track: 'h-[18px] w-[30px]', thumb: 'h-3 w-3',     on: 'translate-x-[15px]', off: 'translate-x-[3px]', spin: 8 }
    : { track: 'h-5 w-9',           thumb: 'h-3.5 w-3.5', on: 'translate-x-[18px]', off: 'translate-x-[3px]', spin: 10 }

  const isDisabled = disabled || loading

  return (
    <label className={cn('inline-flex items-center gap-2 select-none', isDisabled ? 'cursor-not-allowed' : 'cursor-pointer', disabled && 'opacity-50')}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-busy={loading || undefined}
        disabled={isDisabled}
        onClick={() => !isDisabled && onChange(!checked)}
        className={cn(
          'relative inline-flex items-center rounded-full transition-colors duration-200 ease-out cursor-pointer',
          'disabled:cursor-not-allowed',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 focus-visible:ring-offset-1',
          dims.track,
          checked ? 'bg-brand-500' : 'bg-gray-300',
          !isDisabled && (checked ? 'hover:bg-brand-600' : 'hover:bg-gray-400'),
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            'inline-flex items-center justify-center rounded-full bg-white transform transition-transform duration-200 ease-out',
            dims.thumb,
            checked ? dims.on : dims.off,
          )}
        >
          {loading && (
            <Loader2
              size={dims.spin}
              className={cn('animate-spin', checked ? 'text-brand-500' : 'text-gray-400')}
            />
          )}
        </span>
      </button>
      {label && (
        <span className={cn(
          labelSize === 'sm' ? 'text-[11px] text-gray-600' : 'text-sm text-gray-700',
        )}>
          {label}
        </span>
      )}
    </label>
  )
}
